/**
 * finance-core — Cảnh báo tài chính (GĐ1). Một bộ luật duy nhất cho Tổng quan
 * và mọi nơi cần cảnh báo; thuần hàm, có test. Mỗi cảnh báo nói THẲNG việc gì
 * cần làm — không phán xét (một tháng âm chưa hẳn là xấu).
 */
export type AlertLevel = 'bad' | 'warn';
export interface FinancialAlert { id: string; level: AlertLevel; title: string; detail: string }

interface AlertSource { id: string; name?: string; type?: string; balance?: number; usedCredit?: number; creditLimit?: number }
interface AlertDebt { id: string; lender?: string; status?: string; type?: string; remainingBalance?: number; dueDate?: any }

const toDate = (v: any): Date | null => {
  if (!v) return null;
  const d = typeof v.toDate === 'function' ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};
const vnd = (n: number) => `${Math.round(n).toLocaleString('vi-VN')} đ`;

export const financialAlerts = (input: {
  sources: AlertSource[];
  debts: AlertDebt[];
  monthIncome: number;
  monthExpense: number;
  now?: Date;
}): FinancialAlert[] => {
  const { sources, debts, monthIncome, monthExpense } = input;
  const now = input.now ?? new Date();
  const out: FinancialAlert[] = [];

  for (const s of sources) {
    if (s.type === 'credit' && Number(s.creditLimit) > 0) {
      const pct = ((Number(s.usedCredit) || 0) / Number(s.creditLimit)) * 100;
      if (pct >= 80) {
        out.push({
          id: `card:${s.id}`, level: pct >= 90 ? 'bad' : 'warn',
          title: `${/^thẻ(\s|$)/i.test(s.name ?? '') ? s.name : `Thẻ ${s.name}`} đã dùng ${Math.round(pct)}% hạn mức`,
          detail: `Còn khả dụng ${vnd(Number(s.creditLimit) - (Number(s.usedCredit) || 0))}. Trả bớt dư nợ để tránh bị từ chối giao dịch.`
        });
      }
    } else if (s.type !== 'credit' && (Number(s.balance) || 0) < 0) {
      out.push({
        id: `neg:${s.id}`, level: 'warn', title: `Ví ${s.name} đang âm ${vnd(-(Number(s.balance) || 0))}`,
        detail: 'Cập nhật số dư thực tế (Điều chỉnh số dư) hoặc ghi khoản thu còn thiếu.'
      });
    }
  }

  for (const d of debts) {
    if (d.type === 'credit') continue;
    if (d.status === 'overdue') {
      out.push({ id: `overdue:${d.id}`, level: 'bad', title: `Khoản vay ${d.lender ?? ''} đã quá hạn`, detail: `Còn nợ ${vnd(Number(d.remainingBalance) || 0)} — liên hệ bên cho vay hoặc thanh toán sớm.` });
    } else if (d.status === 'active') {
      const due = toDate(d.dueDate);
      if (due) {
        const days = Math.ceil((due.getTime() - now.getTime()) / 86_400_000);
        if (days >= 0 && days <= 7) {
          out.push({ id: `due:${d.id}`, level: 'warn', title: `Khoản vay ${d.lender ?? ''} đến hạn ${days === 0 ? 'hôm nay' : `sau ${days} ngày`}`, detail: `Còn nợ ${vnd(Number(d.remainingBalance) || 0)}.` });
        }
      }
    }
  }

  if (monthIncome > 0 && monthExpense > monthIncome) {
    out.push({
      id: 'month-deficit', level: 'warn', title: `Tháng này chi vượt thu ${vnd(monthExpense - monthIncome)}`,
      detail: 'Một tháng âm chưa hẳn là xấu (có thể do khoản chi lớn một lần) — xem lại các khoản chi lớn trong Giao dịch.'
    });
  }
  return out.sort((a, b) => (a.level === b.level ? 0 : a.level === 'bad' ? -1 : 1));
};
